import { useRef, useState, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import type { MediaItem } from '@/types/posts';
import { PlatformPreviewProps } from '../types';
import { PLATFORM_CONFIG, truncateForPlatform } from '../platformConfig';
import { PreviewCaption } from '../components/PreviewCaption';
import { EmptyState } from '../components/EmptyState';
import { DeviceFrame } from '../components/DeviceFrame';
import { BlurFillImage } from '../components/BlurFillImage';
import { CarouselChevron } from '../components/CarouselChevron';
import { focusSurface } from '@/components/ui/focusStyles';
import { TIKTOK_PHOTO_LIMIT, visualUrl } from '../previewMedia';

const PRIVACY_LABEL_KEYS: Record<string, string> = {
  PUBLIC_TO_EVERYONE: 'preview_tiktok_privacy_public',
  MUTUAL_FOLLOW_FRIENDS: 'preview_tiktok_privacy_friends',
  FOLLOWER_OF_CREATOR: 'preview_tiktok_privacy_followers',
  SELF_ONLY: 'preview_tiktok_privacy_only_me',
};

const SWIPE_THRESHOLD_PX = 40;
const DOT_WINDOW = 7;

/**
 * TikTok Photo Mode: the post's images as a swipeable carousel (TikTok accepts
 * up to 35). Each photo is shown whole (contain) over a blurred copy of itself.
 */
const PhotoModeSlides = ({
  images,
  activeIndex,
  onIndexChange,
}: {
  images: MediaItem[];
  activeIndex?: number;
  onIndexChange?: (index: number) => void;
}) => {
  const { t } = useTranslation('common');
  const [innerIndex, setInnerIndex] = useState(0);
  const startX = useRef<number | null>(null);
  const total = images.length;
  const current = Math.min(activeIndex ?? innerIndex, total - 1);

  const go = (next: number): void => {
    const value = (next + total) % total;
    if (activeIndex === undefined) setInnerIndex(value);
    onIndexChange?.(value);
  };

  const onPointerUp = (e: PointerEvent<HTMLDivElement>): void => {
    if (startX.current === null) return;
    const delta = e.clientX - startX.current;
    startX.current = null;
    if (Math.abs(delta) >= SWIPE_THRESHOLD_PX) go(current + (delta < 0 ? 1 : -1));
  };

  // Sliding window of dots so 35 photos do not overflow the frame
  const windowStart = Math.max(0, Math.min(current - Math.floor(DOT_WINDOW / 2), total - DOT_WINDOW));
  const dotIndexes = Array.from({ length: Math.min(total, DOT_WINDOW) }, (_, i) => windowStart + i);

  return (
    <>
      <div
        className="absolute inset-0 touch-pan-y"
        onPointerDown={(e) => {
          startX.current = e.clientX;
        }}
        onPointerUp={onPointerUp}
        onPointerCancel={() => {
          startX.current = null;
        }}
      >
        <BlurFillImage
          key={current}
          src={visualUrl(images[current])}
          alt={t('preview_tiktok_photo_alt', { index: current + 1 })}
          className="h-full w-full"
          foregroundInset="inset-x-0 top-12 bottom-32"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
      </div>

      {total > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(current - 1)}
            aria-label={t('previous', { defaultValue: 'Previous' })}
            className={`absolute left-1.5 top-[26%] z-10 flex h-[24px] w-[24px] aspect-square items-center justify-center rounded-full bg-black/45 text-white transition-colors before:absolute before:-inset-2.5 before:content-[''] hover:bg-black/65 ${focusSurface}`}
          >
            <CarouselChevron direction="left" />
          </button>
          <button
            type="button"
            onClick={() => go(current + 1)}
            aria-label={t('next', { defaultValue: 'Next' })}
            className={`absolute right-1.5 top-[26%] z-10 flex h-[24px] w-[24px] aspect-square items-center justify-center rounded-full bg-black/45 text-white transition-colors before:absolute before:-inset-2.5 before:content-[''] hover:bg-black/65 ${focusSurface}`}
          >
            <CarouselChevron direction="right" />
          </button>
          <div className="pointer-events-none absolute right-3 top-12 z-10 rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-semibold text-white">
            {current + 1}/{total}
          </div>
          <div className="absolute inset-x-0 bottom-1.5 z-10 flex items-center justify-center gap-1">
            {dotIndexes.map((i) => (
              <span
                key={i}
                aria-hidden="true"
                className={cn(
                  'h-1.5 rounded-full transition-all duration-150',
                  i === current ? 'w-1.5 bg-white' : 'w-1 bg-white/50'
                )}
              />
            ))}
          </div>
        </>
      )}
    </>
  );
};

export const TikTokPreview = ({ caption, media, channel, activeIndex, onIndexChange }: PlatformPreviewProps) => {
  const { t } = useTranslation('common');
  const config = PLATFORM_CONFIG.tiktok;
  const displayCaption = truncateForPlatform(caption, 'tiktok');
  const isEmpty = !caption && media.length === 0;

  const handle = channel?.handle ?? '';
  const options = channel?.options;
  const tiktokTitle = options?.tiktok?.title;
  const privacyLevel = options?.tiktok?.privacyLevel;
  const disableComment = options?.tiktok?.disableComment;

  if (isEmpty) return <EmptyState />;

  // Image posts publish as Photo Mode carousels (no sound, no video chrome)
  const photos = media.filter((m) => m.type === 'image').slice(0, TIKTOK_PHOTO_LIMIT);
  const isPhotoMode = photos.length > 0 && media[0].type === 'image';

  return (
    <DeviceFrame variant="phone">
      <div className="bg-black relative aspect-[9/16]">
        {/* Media background: whole visual over a blurred copy, so nothing is hard-cropped */}
        {isPhotoMode ? (
          <PhotoModeSlides images={photos} activeIndex={activeIndex} onIndexChange={onIndexChange} />
        ) : media.length > 0 ? (
          <div className="absolute inset-0">
            <BlurFillImage src={visualUrl(media[0])} alt={t('preview_tiktok_post_alt')} className="h-full w-full" foregroundInset="left-2 right-12 top-12 bottom-36" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
          </div>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-gray-800 to-gray-900" />
        )}

        {/* Feed tabs */}
        <div className="absolute inset-x-0 top-5 flex items-center justify-center gap-4 text-[12px] font-semibold text-white">
          <span className="text-white/60">{t('preview_tiktok_tab_following')}</span>
          <span className="border-b-2 border-white pb-0.5">{t('preview_tiktok_tab_for_you')}</span>
        </div>

        {/* Privacy badge */}
        {privacyLevel && privacyLevel !== 'PUBLIC_TO_EVERYONE' && (
          <div className="absolute top-12 left-3 bg-black/50 text-white text-[10px] font-medium px-2 py-0.5 rounded-full">
            {PRIVACY_LABEL_KEYS[privacyLevel] ? t(PRIVACY_LABEL_KEYS[privacyLevel]) : privacyLevel}
          </div>
        )}

        {/* Content Overlay */}
        <div className="pointer-events-none absolute inset-0 flex flex-col justify-end p-3.5 pb-5">
          {/* Right Side Actions */}
          <div className="absolute right-2.5 bottom-24 flex flex-col items-center gap-4">
            <div className="flex flex-col items-center">
              {channel?.avatarUrl ? (
                <div className="w-11 h-11 rounded-full bg-gray-800 border-2 border-white overflow-hidden">
                  <img src={channel.avatarUrl} alt={t('preview_tiktok_profile_alt')} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-11 h-11 rounded-full bg-gray-800 border-2 border-white flex items-center justify-center">
                  <span className="text-white text-xs font-bold">{channel?.initials ?? '?'}</span>
                </div>
              )}
              <div className="w-5 h-5 -mt-2 rounded-full bg-[#FE2C55] flex items-center justify-center">
                <Icon name={ICONS.PLUS} size="xs" color="text-white" />
              </div>
            </div>
            <div className="flex flex-col items-center gap-0.5">
              <Icon name={ICONS.HEART} size="custom" className="text-[26px]" color="text-white" />

            </div>
            {!disableComment && (
              <div className="flex flex-col items-center gap-1">
                <Icon name={ICONS.CHAT} size="custom" className="text-[26px]" color="text-white" />

              </div>
            )}
            <div className="flex flex-col items-center gap-0.5">
              <Icon name={ICONS.BOOKMARK} size="custom" className="text-[26px]" color="text-white" />

            </div>
            <div className="flex flex-col items-center gap-0.5">
              <Icon name={ICONS.SHARE} size="custom" className="text-[26px]" color="text-white" />

            </div>
            {/* Spinning disc */}
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <div className="w-6 h-6 rounded-full bg-gray-900 flex items-center justify-center">
                <div className="w-1.5 h-1.5 rounded-full bg-white" />
              </div>
            </div>
          </div>

          {/* Bottom Content */}
          <div className="pr-14">
            <p className="text-white font-semibold text-sm mb-1">@{handle}</p>
            {/* TikTok title (shows above caption if set) */}
            {tiktokTitle && (
              <p className="text-white text-xs font-medium mb-1 line-clamp-1">{tiktokTitle}</p>
            )}
            {caption && (
              <PreviewCaption
                caption={displayCaption}
                hashtagColor={config.hashtagColor}
                className="text-white text-[13px] leading-[17px]"
                maxLines={2}
              />
            )}
            {/* Sound line belongs to a video, not a photo post */}
            {!isPhotoMode && (
              <div className="flex items-center gap-2 mt-2">
                <Icon name={ICONS.TIKTOK} size="xs" color="text-white" />
                <span className="text-white text-xs">{t('preview_tiktok_original_sound')} - {handle}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </DeviceFrame>
  );
};
